import {useEffect, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {MavenLogo} from '@jobseeker/lotus/assets/images';
import {VersionVariants} from '@jobseeker/lotus/enums';
// import {MavenWidget} from '@jobseeker/lotus/components';
import cookie from 'cookie';

import {Chat, TBDialog} from '@components';
import {metaInfo} from '@configs';
import {EXPERIENCE_MAPPING, JOBFEED} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import userService from '@jobseeker/common-core/services/user.service';
import {getExpFilter, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useIsLargeDevice, useAlertCallback} from '@jobseeker/lotus/hooks';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {useMediaQuery, useTheme} from '@mui/material';
import {
  getConfigByApp,
  getLocationIds,
  utilServerSideDeviceDetection,
} from '@utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import type {GetServerSidePropsContext} from 'next/types';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';

import type {AppState} from '@jobseeker/common-core/types';
import ProfileUpdateModal from '@jobseeker/feature-profile/components/ProfileUpdateModal';
// import {loadAds} from '@jobseeker/common-core';

const MavenWidget = dynamic(
  () => import('@jobseeker/feature-profile/components/MavenWidget'),
  {
    ssr: false,
  }
);
type JobFeedProps = {
  filters: {
    page: number;
    loc: string[];
  };
  seoConfig: object;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const JobFeedPage = ({filters, seoConfig}: JobFeedProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBFEED);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch, true);
  const theme = useTheme();
  const isLarge = useIsLargeDevice();
  const isTabDevice = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [isMavenOpen, setShowMaven] = useState(false);
  const [isMavenSubmitSuccess, setMavenSubmit] = useState(false);
  const [isProfileUpdateModalOpen, setIsProfileUpdateModalOpen] =
    useState(false);
  const {
    profile: {showMaven},
  } = useSelector((state: AppState) => state.user);

  const functionalArea = useSelector(
    (state: AppState) => state.user.profile.functionalArea,
    shallowEqual
  );

  const [SkipAlert, toggleSkipAlert] = useAlertCallback({
    primaryCallback: () => {
      console.log('🚀 ~ Cancel:');
    },
    secondaryCallback: () => {
      openUrl('/jobfeed', '_self');
    },
    title: 'Your account is active!',
    subtitle:
      'since you reactivated your account , you may receive communication from us, and your profile is now visible to recruiters',
    secondaryAction: 'Confirm',
    useBottomSheet: true,
  });
  useEffect(() => {
    if (functionalArea?.id == 6 || functionalArea?.id == 1) {
      const lastShownTimestamp = localStorage.getItem(
        'functionalAreaModalLastShown'
      );
      const currentTime = new Date().getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (!lastShownTimestamp) {
        setIsProfileUpdateModalOpen(true);
        localStorage.setItem(
          'functionalAreaModalLastShown',
          currentTime.toString()
        );
      } else {
        const timeDifference = currentTime - parseInt(lastShownTimestamp, 10);

        if (timeDifference >= oneDayInMs) {
          setIsProfileUpdateModalOpen(true);
          localStorage.setItem(
            'functionalAreaModalLastShown',
            currentTime.toString()
          );
        }
      }
    }
  }, [functionalArea]);

  useEffect(() => {
    const haveShownMaven = Boolean(localStorage.getItem('shownMaven'));
    // const mavenVisibility = true && !haveShownMaven;
    const mavenVisibility = !!(showMaven && !haveShownMaven);
    setShowMaven(mavenVisibility);
  }, [showMaven]);

  // useEffect(() => {
  //   if (isMavenOpen) {
  //     // localStorage.setItem('shownMaven', 'true');
  //   }
  // }, [isMavenOpen]);

  const onMavenSubmit = async (formData: any) => {
    const respData: any = await userService.updateDiversity(formData);
    if (respData.message) {
      // enqueueSnackbar(respData.message, {variant: 'success'});
      localStorage.setItem('shownMaven', 'true');
      // setShowMaven(false);
      setMavenSubmit(true);
    }
  };

  const router = useRouter();
  const alertShownRef = useRef(false);
  useEffect(() => {
    if (!router.isReady) return;
    const ref = router.query?.ref as string | undefined;
    if (ref === 'reactivated' && !alertShownRef.current) {
      alertShownRef.current = true;
      toggleSkipAlert();
    }
  }, [router.isReady, router.query?.ref]);
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);

  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);
  const hasFilterChanged = useRef(false);
  const [pageNumber, setPageNumber] = useState(filters?.page + 1);
  let sessionFilter: {minexp?: string; maxexp?: string} = {};
  try {
    sessionFilter = getExpFilter(JOBFEED);
  } catch (error) {}
  const [currentFilters, setCurrentFilters] = useState<{
    loc?: string[];
    minexp?: string;
    maxexp?: string;
    industry?: string[];
  }>({
    loc: filters.loc,
    minexp: (router.query.minexp as string) || (sessionFilter.minexp as string),
    maxexp: (router.query.maxexp as string) || (sessionFilter.maxexp as string),
    industry: (router.query.industry as string)?.split(',') || [],
  });

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  // Reset tracking when filter changes
  useEffect(() => {
    if (initialPageTracked.current) {
      hasFilterChanged.current = true;
    }
  }, [currentFilters]);

  useEffect(() => {
    // console.log('filters: ', currentFilters);

    let actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';
    if (hasFilterChanged.current) {
      actionType = 'filter';
    }

    if (userId && isLoggedIn) {
      hasPageNoChanged.current = false;
      hasFilterChanged.current = false;
      TrackEventUBA('jobfeedPageView', actionType, {
        ref: router.query.ref || '',
        // filterExp: router.query.minexp
        //   ? `${router.query.minexp}-${router.query.maxexp}`
        //   : '',
        // filterLocation: filters?.loc?.join(',') || '',
        filterExp: currentFilters?.maxexp
          ? `${currentFilters.minexp}-${currentFilters.maxexp}`
          : '',
        filterLocation: currentFilters?.loc?.join(',') || '',
        filterIndustry: currentFilters?.industry?.join(',') || '',
        pageName: 'Jobfeed',
        pageNo: pageNumber,
        sessionUsername: `${sessionStorage.getItem('userId') || userId}`,
      });
      initialPageTracked.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, pageNumber, currentFilters]);

  // useEffect(() => {
  //   // Add meta tags to prevent caching
  //   const metaTags = [
  //     {
  //       httpEquiv: 'Cache-Control',
  //       content: 'no-cache, no-store, must-revalidate',
  //     },
  //     {httpEquiv: 'Pragma', content: 'no-cache'},
  //     {httpEquiv: 'Expires', content: '0'},
  //   ];

  //   metaTags.forEach(tag => {
  //     const meta = document.createElement('meta');
  //     meta.setAttribute('http-equiv', tag.httpEquiv);
  //     meta.setAttribute('content', tag.content);
  //     document.head.appendChild(meta);
  //   });

  //   // Clear browser cache for this page
  //   if ('caches' in window) {
  //     caches.keys().then(cacheNames => {
  //       cacheNames.forEach(cacheName => {
  //         caches.open(cacheName).then(cache => {
  //           cache.keys().then(requests => {
  //             requests.forEach(request => {
  //               if (request.url.includes('/jobfeed')) {
  //                 cache.delete(request);
  //               }
  //             });
  //           });
  //         });
  //       });
  //     });
  //   }

  //   return () => {
  //     metaTags.forEach(tag => {
  //       const metas = document.querySelectorAll(
  //         `meta[http-equiv="${tag.httpEquiv}"]`
  //       );
  //       metas.forEach(meta => meta.remove());
  //     });
  //   };
  // }, []);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout showbottom={true}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={[]}
          >
            <TBDialog />
            {isAppIimjobs ? (
              <ProfileUpdateModal isJobFeed={true} />
            ) : (
              <ProfileUpdateModal
                isJobFeed={true}
                isProfileUpdateModalOpen={isProfileUpdateModalOpen}
              />
            )}
            <JobFeed
              jobFeedType={JOBFEED}
              catOrTagId={''}
              catOrTagTitle={'My Jobfeed'}
              showProfileCard={isTabDevice ? false : true}
              showFeatured={false}
              showLearning={false}
              showLearningCenterCTA={false}
              preFilters={filters}
              setPageNumber={setPageNumber}
              onFiltersChange={setCurrentFilters}
            />
            {isLarge && isAppIimjobs && (
              <MavenWidget
                show={isMavenOpen}
                imgUrl={MavenLogo}
                onClose={() => setShowMaven(false)}
                onSubmit={onMavenSubmit}
                isSuccess={isMavenSubmitSuccess}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <SkipAlert />
      {!isMavenOpen && <Chat />}
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    // context.res?.setHeader(
    //   'Cache-Control',
    //   'public, s-maxage=10, stale-while-revalidate=59'
    // );
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: JOBFEED},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    store.dispatch({
      type: 'job/jobFeed/set_jobfeed_loading',
      payload: {isLoading: true, loadingSource: 'first_load'},
    });
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const query = parseInt(context.query?.maxexp) || 0;
    const posting = parseInt(context.query?.posting);
    const sort = context.query?.sort || null;
    const location = getLocationIds(context.query?.loc || '');
    const ref = context.query?.ref || '';
    const campaignLogin = context.query?.campaignLogin || '';

    const locationIds = location
      ? location?.split(',')?.map(v => parseInt(v))
      : null;

    const filters = {
      sort: sort ? [sort] : null,
      page: pageNo > 0 ? pageNo - 1 : 0,
      query: query,
      minexp: minExp,
      maxexp: maxExp,
      loc: locationIds,
      posting,
    };
    const seoConfig = await loadSEO(JOBFEED);

    // Add cache control headers to prevent browser caching
    if (context.res) {
      context.res.setHeader(
        'Cache-Control',
        'no-store, no-cache, must-revalidate, proxy-revalidate'
      );
      context.res.setHeader('Pragma', 'no-cache');
      context.res.setHeader('Expires', '0');
    }
    // Check if user has cookies
    if (context.req?.headers?.cookie) {
      const cookies = cookie.parse(context.req.headers.cookie);

      // Parse experience filter from cookie if it exists
      let experienceFilterCookie = null;
      if (isAppIimjobs) {
        try {
          experienceFilterCookie = cookies['filter']
            ? JSON.parse(cookies['filter'])
            : null;
        } catch (error) {
          console.error('Error parsing filter cookie:', error);
        }
      }

      // Check if user is authenticated
      const isAuthenticated = !!cookies?.[COOKIE_LOGIN_AUTH];
      const randomKey = Math.random().toString(36).substring(2, 15);
      const rQueryParam = context.query?._r as string;

      // Determine if we need to apply experience filters
      const shouldApplyExperienceFilter =
        // Either we have a valid cookie filter or user is authenticated
        experienceFilterCookie ||
        isAuthenticated ||
        (rQueryParam && rQueryParam === randomKey);
      if (shouldApplyExperienceFilter) {
        let targetMinExp = 0;
        let targetMaxExp = 0;

        // Priority 1: Use experience filter from cookie if available and valid
        if (experienceFilterCookie?.minexp && experienceFilterCookie?.maxexp) {
          targetMinExp = Number(experienceFilterCookie.minexp);
          targetMaxExp = Number(experienceFilterCookie.maxexp);
        }
        // Priority 2: Use URL parameters if available
        else if (minExp || maxExp) {
          targetMinExp = minExp;
          targetMaxExp = maxExp;
        }
        // Priority 3: Get experience from user profile if authenticated
        else if (isAuthenticated) {
          try {
            const headers = {
              cookie: context.req.headers.cookie,
              'user-agent': context.req?.headers?.['user-agent'],
            };
            const {profile} = await userService.profile('profile', headers);
            const userExp = Math.floor(
              (profile?.experienceYear ?? 0) +
                (profile?.experienceMonth ?? 0) / 10
            );

            if (userExp !== undefined && userExp !== null) {
              // Find the appropriate experience range based on user's experience
              const expRange = EXPERIENCE_MAPPING.find(
                (item: any, index) =>
                  userExp >= item.minexp && userExp <= item.maxexp && index != 0
              );

              if (expRange) {
                targetMinExp = Number(expRange.minexp);
                targetMaxExp = Number(expRange.maxexp);
              } else {
                // Fallback to default range if no matching range found
                targetMinExp = 0;
                targetMaxExp = 30;
              }
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
          }
        }

        // Check if we need to redirect
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const shouldRedirect =
          targetMinExp !== undefined &&
          targetMaxExp !== undefined &&
          // Only redirect if the values are actually different
          (targetMinExp !== minExp || targetMaxExp !== maxExp) &&
          // Don't redirect if both current and target are effectively 0
          !(
            targetMinExp === 0 &&
            targetMaxExp === 0 &&
            minExp === 0 &&
            maxExp === 0
          );

        if (shouldRedirect) {
          // Preserve existing query parameters
          const queryParams = new URLSearchParams();

          // Add experience filters
          queryParams.set('minexp', targetMinExp.toString());
          queryParams.set('maxexp', targetMaxExp.toString());

          // Add page number if it exists
          if (pageNo) {
            queryParams.set('page', context.query?.page.toString());
          }

          // Add other existing query parameters
          if (sort) queryParams.set('sort', context.query?.sort as string);
          if (location) queryParams.set('loc', context.query?.loc as string);

          // Add random parameter to prevent caching
          if (isAppIimjobs) {
            queryParams.set('_r', randomKey);
          }
          ref && queryParams.set('ref', ref);
          if (!isAppIimjobs) {
            campaignLogin && queryParams.set('campaignLogin', campaignLogin);
          }
          console.log(queryParams, 'expRangequeryParams');
          return {
            redirect: {
              permanent: false,
              destination: `/jobfeed?${queryParams.toString()}`,
            },
            props: {
              filters,
              seoConfig,
            },
          };
        }
      }
    }

    return {
      props: {
        filters,
        seoConfig,
      },
    };
  }
);

export default JobFeedPage;
